import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type PointerEvent as ReactPointerEvent } from "react";
import { ParticleField } from "./ParticleField";
import { ActionSurface } from "./ActionSurface";
import type { Environment, Point, WalterSnapshot, WalterState } from "../core/types";
import { walterController } from "../core/walterController";
import {
  gestureModeAt,
  overlayFromWire,
  overlayToWire,
  resolveSpatialRelease,
  spatialProgress,
  type SpatialGestureMode,
  type SpatialOverlay,
} from "../core/spatialNavigation";

interface MainDisplayProps {
  snapshot: WalterSnapshot;
  safeArea: boolean;
  onFps: (fps: number) => void;
  onTouch: (point: Point) => void;
  compact?: boolean;
}

interface GestureOrigin {
  pointerId: number;
  x: number;
  y: number;
  time: number;
  moved: boolean;
  held: boolean;
  mode: SpatialGestureMode;
  height: number;
  lastX: number;
  lastY: number;
  lastTime: number;
}

const menuItems: Array<{ label: string; glyph: string; detail: string; angle: number; surface: "talk" | "vision" | "memory" | "tools" | "news" | "system" }> = [
  { label: "Talk", glyph: "◌", detail: "VOICE", angle: -90, surface: "talk" },
  { label: "See", glyph: "◉", detail: "VISION", angle: -30, surface: "vision" },
  { label: "Recall", glyph: "⌁", detail: "MEMORY", angle: 30, surface: "memory" },
  { label: "Tasks", glyph: "⌘", detail: "AGENT", angle: 90, surface: "tools" },
  { label: "Brief", glyph: "⋮", detail: "SIGNALS", angle: 150, surface: "news" },
  { label: "Body", glyph: "◇", detail: "SYSTEM", angle: 210, surface: "system" },
];

const menuStatus = (snapshot: WalterSnapshot, surface: (typeof menuItems)[number]["surface"]) => {
  if (surface === "talk") return snapshot.audio.listening ? "LIVE" : snapshot.output.speaking ? "VOICE" : "READY";
  if (surface === "vision") return snapshot.vision.privacy === "open" ? "OPEN" : "PRIVATE";
  if (surface === "memory") return String(snapshot.memories.items.length).padStart(2, "0");
  if (surface === "tools") return String(snapshot.timers.items.filter((timer) => timer.status === "running").length).padStart(2, "0");
  if (surface === "news") return snapshot.news.stale ? "CACHED" : "FRESH";
  return snapshot.system.lowVoltage ? "CHECK" : snapshot.connection.state === "connected" ? "LIVE" : "LOCAL";
};

const environmentCopy: Record<Environment, { label: string; detail: string }> = {
  walter: { label: "PRESENCE", detail: "Walter is here" },
  vision: { label: "VISION", detail: "Optical channel standing by" },
  news: { label: "SIGNAL", detail: "No urgent transmissions" },
  system: { label: "SYSTEM", detail: "Embodiment nominal" },
};

const stateWhispers: Record<WalterState, string> = {
  idle: "I am here.",
  aware: "I see you.",
  listening: "Go ahead.",
  thinking: "Following the thread…",
  tool: "Reaching beyond the shell…",
  speaking: "Signal becoming voice…",
  sleeping: "Quiet orbit.",
};

export function MainDisplay({ snapshot, safeArea, onFps, onTouch, compact = false }: MainDisplayProps) {
  const [booting, setBooting] = useState(true);
  const [overlay, setOverlayState] = useState<SpatialOverlay>(() => overlayFromWire(snapshot.view.overlay));
  const [dragVisual, setDragVisual] = useState<{ target: "conversation" | "controls"; progress: number } | null>(null);
  const [pressing, setPressing] = useState(false);
  const [wakeArmed, setWakeArmed] = useState(false);
  const [draft, setDraft] = useState("");
  const origin = useRef<GestureOrigin | null>(null);
  const longTimer = useRef<number | null>(null);
  const wakeTimer = useRef<number | null>(null);
  const motionTimer = useRef<number | null>(null);
  const lastTap = useRef(0);
  const suppressEdgeClick = useRef(false);
  const displayRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setBooting(false), 2400);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => () => {
    if (longTimer.current) window.clearTimeout(longTimer.current);
    if (wakeTimer.current) window.clearTimeout(wakeTimer.current);
    if (motionTimer.current) window.clearTimeout(motionTimer.current);
  }, []);

  useEffect(() => {
    if (snapshot.view.overlay === "approval") return;
    setOverlayState(overlayFromWire(snapshot.view.overlay));
  }, [snapshot.view.overlay]);

  useEffect(() => {
    if (snapshot.view.surface === "presence") return;
    setOverlayState("none");
    setDragVisual(null);
  }, [snapshot.view.surface]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" && event.key !== "Backspace") return;
      if (snapshot.approval) return;
      if (overlay !== "none") {
        event.preventDefault();
        setOverlay("none");
      } else if (snapshot.view.surface !== "presence") {
        event.preventDefault();
        walterController.navigate("presence");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const setOverlay = (next: SpatialOverlay) => {
    setOverlayState(next);
    if (next !== "none" && snapshot.view.surface !== "presence") walterController.navigate("presence");
    walterController.setOverlay(overlayToWire(next));
  };

  const relativePoint = (event: ReactPointerEvent) => {
    const rect = displayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1)),
      y: Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1)),
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>, forcedMode?: SpatialGestureMode) => {
    if (!forcedMode && (event.target as HTMLElement).closest("button,input,textarea,select,a,video")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const now = performance.now();
    const rect = displayRef.current?.getBoundingClientRect();
    const height = rect?.height ?? 720;
    const localY = event.clientY - (rect?.top ?? 0);
    const mode = forcedMode ?? gestureModeAt(overlay, localY, height);
    origin.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, time: now, moved: false, held: false, mode, height, lastX: event.clientX, lastY: event.clientY, lastTime: now };
    const point = relativePoint(event);
    onTouch(point);
    if (mode === "gaze" && overlay === "none" && snapshot.view.surface === "presence") {
      setPressing(true);
      longTimer.current = window.setTimeout(() => {
        if (!origin.current || origin.current.moved || snapshot.state === "sleeping") return;
        origin.current.held = true;
        setOverlay("none");
        walterController.startListening();
        navigator.vibrate?.(18);
      }, 390);
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const point = relativePoint(event);
    onTouch(point);
    if (!origin.current) return;
    const now = performance.now();
    const elapsed = Math.max(8, now - origin.current.lastTime);
    const velocityX = Math.max(-1, Math.min(1, (event.clientX - origin.current.lastX) / elapsed * .18));
    const velocityY = Math.max(-1, Math.min(1, (event.clientY - origin.current.lastY) / elapsed * .18));
    const energy = Math.min(1, Math.hypot(velocityX, velocityY));
    window.requestAnimationFrame(() => {
      displayRef.current?.style.setProperty("--velocity-x", String(velocityX));
      displayRef.current?.style.setProperty("--velocity-y", String(velocityY));
      displayRef.current?.style.setProperty("--pointer-energy", String(energy));
    });
    origin.current.lastX = event.clientX; origin.current.lastY = event.clientY; origin.current.lastTime = now;
    const distance = Math.hypot(event.clientX - origin.current.x, event.clientY - origin.current.y);
    if (distance > 9) {
      origin.current.moved = true;
      if (origin.current.mode !== "gaze") suppressEdgeClick.current = true;
      if (longTimer.current) window.clearTimeout(longTimer.current);
      setPressing(false);
    }
    if (origin.current.mode === "gaze") {
      walterController.setGaze(point.x, point.y);
      return;
    }
    const dy = event.clientY - origin.current.y;
    const progress = spatialProgress(origin.current.mode, dy, origin.current.height);
    const target = origin.current.mode.includes("conversation") ? "conversation" : "controls";
    setDragVisual({ target, progress });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    if (longTimer.current) window.clearTimeout(longTimer.current);
    setPressing(false);
    const start = origin.current;
    origin.current = null;
    if (motionTimer.current) window.clearTimeout(motionTimer.current);
    motionTimer.current = window.setTimeout(() => {
      displayRef.current?.style.setProperty("--velocity-x", "0");
      displayRef.current?.style.setProperty("--velocity-y", "0");
      displayRef.current?.style.setProperty("--pointer-energy", "0");
    }, 260);
    if (!start) return;
    if (start.held) {
      walterController.stopListening();
      navigator.vibrate?.(10);
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    const distance = Math.hypot(dx, dy);
    if (start.mode !== "gaze" && distance > 9) {
      const elapsed = Math.max(16, performance.now() - start.time);
      const result = resolveSpatialRelease(overlay, start.mode, dy, dy / elapsed, start.height);
      setDragVisual(null);
      setOverlay(result.overlay);
      if (result.completed) navigator.vibrate?.(12);
      return;
    }
    setDragVisual(null);
    if (distance > 9) return;
    if (overlay !== "none") {
      setOverlay("none");
      return;
    }
    const now = performance.now();
    if (snapshot.state === "sleeping") {
      if (now - lastTap.current < 520) {
        lastTap.current = 0;
        setWakeArmed(false);
        if (wakeTimer.current) window.clearTimeout(wakeTimer.current);
        walterController.wake();
        navigator.vibrate?.([12, 32, 16]);
      } else {
        lastTap.current = now;
        setWakeArmed(true);
        if (wakeTimer.current) window.clearTimeout(wakeTimer.current);
        wakeTimer.current = window.setTimeout(() => setWakeArmed(false), 900);
      }
      return;
    }
    setOverlay("menu");
    navigator.vibrate?.(7);
  };

  const submitTurn = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    walterController.submitText(text);
    setDraft("");
  };

  const gazeStyle = {
    "--gaze-x": snapshot.gaze.x,
    "--gaze-y": snapshot.gaze.y,
    "--audio": snapshot.settings.muted ? 0 : snapshot.audioLevel,
    "--brightness": snapshot.view.brightness,
    "--mic": snapshot.audio.micLevel,
    "--voice": snapshot.audio.outputLevel,
    "--attention": snapshot.attention.confidence,
    "--transfer-energy": snapshot.transfer?.energy ?? 0,
    "--transfer-duration": `${snapshot.transfer?.durationMs ?? 1700}ms`,
  } as CSSProperties;

  const conversationProgress = dragVisual?.target === "conversation" ? dragVisual.progress : overlay === "conversation" ? 1 : 0;
  const controlsProgress = dragVisual?.target === "controls" ? dragVisual.progress : overlay === "controls" ? 1 : 0;
  const conversationStyle = {
    "--sheet-offset": `${(1 - conversationProgress) * 116}%`,
    "--sheet-opacity": conversationProgress,
  } as CSSProperties;
  const controlsStyle = {
    "--sheet-offset": `${(1 - controlsProgress) * -116}%`,
    "--sheet-opacity": controlsProgress,
  } as CSSProperties;
  const recentTurns = snapshot.conversation.slice(-2);

  const expression = snapshot.state === "sleeping"
    ? "sleep"
    : snapshot.state === "speaking"
      ? "speak-soft"
      : snapshot.emotion === "amused"
        ? "amused"
        : snapshot.gaze.y < -.55
          ? "look-up"
          : snapshot.gaze.x < -.48
            ? "look-left"
            : snapshot.gaze.x > .48
              ? "look-right"
              : "neutral";

  return (
    <section
      ref={displayRef}
      className={`walter-display state-${snapshot.state} env-${snapshot.environment} emotion-${snapshot.emotion} surface-${snapshot.view.surface} expression-${expression} transfer-${snapshot.transfer?.phase ?? "none"} direction-${snapshot.transfer?.direction ?? "none"} quality-${snapshot.settings.performanceMode} overlay-${overlay} ${booting ? "is-booting" : ""} ${overlay === "menu" ? "menu-open" : ""} ${pressing ? "is-pressing" : ""} ${dragVisual ? `is-pulling pull-${dragVisual.target}` : ""} ${wakeArmed ? "wake-armed" : ""} ${compact ? "is-compact" : ""}`}
      data-testid="walter-display"
      data-state={snapshot.state}
      data-environment={snapshot.environment}
      data-transfer={snapshot.transfer?.id ?? "none"}
      aria-label={`Walter, ${snapshot.state}`}
      style={gazeStyle}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        if (longTimer.current) window.clearTimeout(longTimer.current);
        if (origin.current?.held) walterController.stopListening();
        origin.current = null;
        setPressing(false);
        setDragVisual(null);
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="portal-foundation" />
      <div className="boot-sequence" aria-hidden="true"><i /><i /><i /><span>WALTER / ARRIVING</span></div>
      <ParticleField snapshot={snapshot} onFps={onFps} />

      <div className="environment-field" aria-hidden="true">
        <div className="env-scan-line" />
        <svg viewBox="0 0 720 720">
          <circle cx="360" cy="360" r="317" />
          <circle cx="360" cy="360" r="284" />
          <path d="M73 360H173M547 360H647M360 73V173M360 547V647" />
          <path className="env-glyph" d="M360 105L382 145L360 185L338 145Z M535 360L575 382L615 360L575 338Z M360 535L382 575L360 615L338 575Z M105 360L145 382L185 360L145 338Z" />
        </svg>
      </div>

      <div className="face-system" aria-hidden="true">
        <div className="face-aura" />
        <img className="walter-portrait" src="/walter-v7-canonical.webp" alt="" draggable="false" />
        <div className="walter-expression" />
        <div className="walter-expression speech-expression-open" />
        <img className="walter-portrait portrait-echo portrait-echo-digital" src="/walter-v7-canonical.webp" alt="" draggable="false" />
        <img className="walter-portrait portrait-echo portrait-echo-organic" src="/walter-v7-canonical.webp" alt="" draggable="false" />
        <div className="face-shadow" />
        <div className="face-biometric"><i /><i /><i /><i /></div>
        <svg className="circuit-veil" viewBox="0 0 720 720">
          <g>
            <path d="M431 130L481 173L473 226L535 247L516 302L575 335" />
            <path d="M463 187L516 196L542 159M488 267L548 284L588 256" />
            <path d="M429 359L490 349L526 390L579 382" />
            <path d="M445 426L505 448L520 505L580 526" />
            <path d="M414 498L467 520L469 578L521 607" />
            <circle cx="481" cy="173" r="4" /><circle cx="535" cy="247" r="4" /><circle cx="575" cy="335" r="4" />
            <circle cx="490" cy="349" r="4" /><circle cx="579" cy="382" r="4" /><circle cx="505" cy="448" r="4" />
          </g>
        </svg>
        <div className="eye eye-left"><span className="iris"><i /></span><b className="eyelid" /></div>
        <div className="eye eye-right"><span className="iris"><i /></span><b className="eyelid" /></div>
        <div className="mouth-signal" />
        <div className="voice-resonance"><i /><i /><i /></div>
        <div className="awareness-bloom"><i /><i /><b /></div>
        <div className="speech-field"><i /><i /><i /><b /></div>
        <div className="thought-crown"><i /><i /><b /></div>
        <div className="optical-caustic" />
      </div>

      <div className="touch-lens" aria-hidden="true" />
      <div className="chromatic-tide" aria-hidden="true"><i /><i /></div>

      <div key={snapshot.transfer?.id ?? snapshot.tool.nonce} className={`tool-transfer tool-${snapshot.tool.phase} transfer-${snapshot.transfer?.phase ?? "none"}`} aria-hidden="true">
        <i className="phase-filament" /><b className="phase-fold" /><span>{snapshot.tool.type === "vision" ? "◉" : snapshot.tool.type === "message" ? "↗" : "⌁"}</span>
      </div>
      <div key={`body-channel-${snapshot.transfer?.id ?? snapshot.tool.nonce}-${snapshot.state}`} className={`body-channel tool-${snapshot.tool.phase} state-${snapshot.state} transfer-${snapshot.transfer?.phase ?? "none"} direction-${snapshot.transfer?.direction ?? "none"}`} aria-hidden="true">
        <i className="body-packet tool-packet">{snapshot.tool.type === "vision" ? "◉" : snapshot.tool.type === "message" ? "↗" : "⌁"}</i>
        <i className="body-packet somatic-packet" />
        <b className="body-axis" />
      </div>

      <div className="listening-ripples" aria-hidden="true"><i /><i /><i /></div>
      <div className="state-whisper" aria-live="polite">
        {snapshot.output.speaking ? snapshot.output.text.slice(0, 72) : snapshot.task.label || stateWhispers[snapshot.state]}
      </div>
      <div className="wake-prompt" aria-live="polite"><i />TOUCH AGAIN <span>WAKE WALTER</span></div>
      <div className="environment-readout" aria-hidden="true">
        <span>{environmentCopy[snapshot.environment].label}</span>
        <p>{environmentCopy[snapshot.environment].detail}</p>
      </div>

      <nav className="radial-menu energy-halo" aria-label="Walter menu">
        <svg className="halo-geometry" viewBox="0 0 720 720" aria-hidden="true">
          <circle className="halo-quiet-ring" cx="360" cy="360" r="309" />
          {menuItems.map((item, index) => <circle key={item.label} className={`halo-segment halo-segment-${index + 1}`} cx="360" cy="360" r="292" pathLength="100" style={{ "--segment-index": index } as CSSProperties} />)}
        </svg>
        {menuItems.map((item, index) => (
          <button
            key={item.label}
            style={{ "--angle": `${item.angle}deg`, "--segment-index": index } as CSSProperties}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => { setOverlay("none"); walterController.navigate(item.surface); }}
            aria-label={`${item.label}, ${menuStatus(snapshot, item.surface)}`}
          >
            <em>{menuStatus(snapshot, item.surface)}</em><strong>{item.glyph}</strong><span>{item.label}</span><small>{item.detail}</small>
          </button>
        ))}
      </nav>
      <div className="rim-affordance" aria-hidden="true"><i />TAP FOR ORGANS <b /> HOLD TO SPEAK</div>

      <nav className="spatial-edges" aria-label="Walter edge controls">
        <button
          className={`spatial-edge edge-controls ${overlay === "controls" ? "is-active" : ""}`}
          onPointerDown={(event) => {
            event.stopPropagation();
            suppressEdgeClick.current = false;
            onPointerDown(event, overlay === "controls" ? "close-controls" : "open-controls");
          }}
          onPointerMove={(event) => { event.stopPropagation(); onPointerMove(event); }}
          onPointerUp={(event) => { event.stopPropagation(); onPointerUp(event); }}
          onClick={() => {
            if (suppressEdgeClick.current) { suppressEdgeClick.current = false; return; }
            setOverlay(overlay === "controls" ? "none" : "controls");
          }}
          aria-expanded={overlay === "controls"}
          aria-controls="walter-controls"
        ><i /><span>{overlay === "controls" ? "CLOSE" : "CONTROL"}</span></button>
        <button
          className={`spatial-edge edge-conversation ${overlay === "conversation" ? "is-active" : ""}`}
          onPointerDown={(event) => {
            event.stopPropagation();
            suppressEdgeClick.current = false;
            onPointerDown(event, overlay === "conversation" ? "close-conversation" : "open-conversation");
          }}
          onPointerMove={(event) => { event.stopPropagation(); onPointerMove(event); }}
          onPointerUp={(event) => { event.stopPropagation(); onPointerUp(event); }}
          onClick={() => {
            if (suppressEdgeClick.current) { suppressEdgeClick.current = false; return; }
            setOverlay(overlay === "conversation" ? "none" : "conversation");
          }}
          aria-expanded={overlay === "conversation"}
          aria-controls="walter-conversation"
        ><span>{overlay === "conversation" ? "CLOSE" : "THREAD"}</span><i /></button>
      </nav>

      <div className={`neural-tether tether-${dragVisual?.target ?? "none"}`} style={{ "--pull": dragVisual?.progress ?? 0 } as CSSProperties} aria-hidden="true">
        <i /><b /><span>{dragVisual?.target === "conversation" ? "THREAD" : "CONTROL"}</span>
      </div>

      <ActionSurface snapshot={snapshot} surface={snapshot.view.surface} onClose={() => walterController.navigate("presence")} />

      {snapshot.approval && <section className="approval-gate" onPointerDown={(event) => event.stopPropagation()}>
        <span>WALTER REQUIRES CONSENT</span><h2>{snapshot.approval.title}</h2><p>{snapshot.approval.detail}</p>
        <div><button onClick={() => walterController.resolveApproval(false)}>DENY</button><button onClick={() => walterController.resolveApproval(true)}>APPROVE ONCE</button></div>
      </section>}

      <section id="walter-conversation" className={`gesture-sheet spatial-sheet conversation-sheet ${conversationProgress > .02 ? "is-visible" : ""}`} style={conversationStyle} aria-hidden={conversationProgress <= .02} aria-label="Current conversation">
        <button
          type="button"
          className="sheet-handle"
          aria-label="Pull down to close conversation"
          onPointerDown={(event) => { event.stopPropagation(); suppressEdgeClick.current = false; onPointerDown(event, "close-conversation"); }}
          onPointerMove={(event) => { event.stopPropagation(); onPointerMove(event); }}
          onPointerUp={(event) => { event.stopPropagation(); onPointerUp(event); }}
          onClick={() => { if (suppressEdgeClick.current) { suppressEdgeClick.current = false; return; } setOverlay("none"); }}
        ><i /></button>
        <div className="thread-heading">
          <span className="sheet-kicker">CURRENT THREAD <b>{snapshot.brain.status}</b></span>
          <small>{snapshot.connection.state} · seq {snapshot.seq}</small>
          <button onPointerDown={(event) => event.stopPropagation()} onClick={() => setOverlay("none")} aria-label="Close conversation">↓</button>
        </div>
        <div className="thread-transcript" aria-live="polite">
          {recentTurns.map((turn) => <div className="thread-turn" key={turn.id}><p className="thread-user"><span>YOU</span>{turn.user}</p><p className="thread-walter"><span>WALTER</span>{turn.walter}</p></div>)}
          {snapshot.input.transcript && <p className="thread-user"><span>YOU</span>{snapshot.input.transcript}</p>}
          {snapshot.output.text && <p className="thread-walter"><span>WALTER</span>{snapshot.output.text}</p>}
          {!recentTurns.length && !snapshot.input.transcript && !snapshot.output.text && <p className="thread-empty">Hold the voice key or send Walter a thought.</p>}
        </div>
        <form className="thread-input" onSubmit={submitTurn}>
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask time, system status, or say remember…"
            aria-label="Message Walter"
          />
          <button className={`thread-voice ${snapshot.audio.listening ? "is-active" : ""}`} type="button" onPointerDown={(event) => { event.stopPropagation(); walterController.startListening(); }} onPointerUp={(event) => { event.stopPropagation(); walterController.stopListening(); }}>◌<span>{snapshot.audio.listening ? "HEARING" : "HOLD"}</span></button>
          <button type="submit">SEND</button>
          {snapshot.task.id && <button type="button" onClick={walterController.cancelTurn}>CANCEL</button>}
        </form>
      </section>

      <section id="walter-controls" className={`gesture-sheet spatial-sheet controls-sheet ${controlsProgress > .02 ? "is-visible" : ""}`} style={controlsStyle} aria-hidden={controlsProgress <= .02} aria-label="Quick controls">
        <button
          type="button"
          className="sheet-handle"
          aria-label="Pull up to close controls"
          onPointerDown={(event) => { event.stopPropagation(); suppressEdgeClick.current = false; onPointerDown(event, "close-controls"); }}
          onPointerMove={(event) => { event.stopPropagation(); onPointerMove(event); }}
          onPointerUp={(event) => { event.stopPropagation(); onPointerUp(event); }}
          onClick={() => { if (suppressEdgeClick.current) { suppressEdgeClick.current = false; return; } setOverlay("none"); }}
        ><i /></button>
        <div className="controls-heading"><span className="sheet-kicker">QUICK CONTROL <b>LOCAL</b></span><button onPointerDown={(event) => event.stopPropagation()} onClick={() => setOverlay("none")} aria-label="Close controls">↑</button></div>
        <label>LIGHT <input type="range" min="0.35" max="1" step="0.01" value={snapshot.settings.brightness} onChange={(event) => walterController.setSetting("brightness", Number(event.target.value))} /></label>
        <label>VOICE <input type="range" min="0" max="1" step="0.01" value={snapshot.settings.volume} onChange={(event) => walterController.setSetting("volume", Number(event.target.value))} /></label>
        <div className="quick-control-grid">
          <button className={snapshot.settings.muted ? "is-active" : ""} onClick={() => walterController.setSetting("muted", !snapshot.settings.muted)}><i>◌</i>{snapshot.settings.muted ? "UNMUTE" : "MUTE"}</button>
          <button className={snapshot.vision.privacy === "closed" ? "is-active" : ""} onClick={() => snapshot.vision.privacy === "open" ? walterController.setPrivacy("closed") : (setOverlay("none"), walterController.navigate("vision"))}><i>◉</i>{snapshot.vision.privacy === "open" ? "CLOSE EYE" : "VISION"}</button>
          <button className={snapshot.settings.cameraTracking ? "is-active" : ""} onClick={() => walterController.setSetting("cameraTracking", !snapshot.settings.cameraTracking)}><i>⌖</i>TRACK</button>
          <button onClick={() => walterController.setSetting("performanceMode", snapshot.settings.performanceMode === "eco" ? "live" : "eco")}><i>⌁</i>{snapshot.settings.performanceMode.toUpperCase()}</button>
          <button onClick={() => { setOverlay("none"); walterController.navigate("system"); }}><i>◇</i>BODY</button>
          <button className="danger-soft" onClick={snapshot.state === "sleeping" ? walterController.wake : walterController.sleep}><i>○</i>{snapshot.state === "sleeping" ? "WAKE" : "SLEEP"}</button>
        </div>
      </section>

      {safeArea && <div className="safe-area" aria-hidden="true"><span>SAFE AREA</span></div>}
      <div className={`connection-readout is-${snapshot.connection.state}`} aria-label={`Nervous system ${snapshot.connection.state}`}>
        <i />{snapshot.connection.state}
      </div>
      <div className="glass-surface" aria-hidden="true" />
    </section>
  );
}
